import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { FileCode, Upload, AlertCircle, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/supabase';
import { saveBlobWithPicker, openManualDownload } from '../lib/fileSave';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_TONE = {
  critical: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  major: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  minor: 'bg-accent/10 text-accent border-accent/30',
};

/**
 * IdsExportImportButtons — Sprint 5 hooks for IDS round-trip.
 *
 * - Export: downloads the project's active confirmed_rules as an IDS 1.0
 *   XML file. The backend filters to is_active=true and returns 409 if
 *   the project has none.
 * - Import: uploads an IDS file, shows the parsed candidates +
 *   per-spec warnings in a preview modal, then asks the user to
 *   confirm. No rules are written until the user clicks Confirm.
 *
 * The component is self-contained so ProjectDetailPage doesn't grow
 * further. It writes via the existing `/api/projects/{id}/confirm-rules`
 * endpoint to keep one governance path for rule confirmation.
 */
export default function IdsExportImportButtons({ projectId, projectName, projectCode, onImported, readOnly = false }) {
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);  // { rules, warnings, summary }
  const fileInputRef = useRef(null);

  // ── Export ────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExportLoading(true);
    setError('');
    // Always give the user immediate, visible feedback that the click
    // registered. The previous version went silent for the duration of
    // the fetch, which on a fast-and-quiet browser made it look like
    // the click did nothing.
    const t = toast.loading('Generating IDS file…', {
      description: `Project ${projectCode || ''} active rules → IDS 1.0 XML`,
    });
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/ids/export`,
        { headers: { Accept: 'application/xml' } },
      );
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        let detail = txt;
        try { detail = JSON.parse(txt).detail || txt; } catch (_) { /* keep raw */ }
        // 409 is the governance gate — zero active rules on this project.
        if (resp.status === 409) {
          toast.dismiss(t);
          toast.warning('No active rules to export', {
            description: 'Confirm BEP rules first, then try again.',
            duration: 6000,
          });
          setError('Cannot export — this project has no active rules. Confirm BEP candidates first.');
          return;
        }
        throw new Error(`IDS export failed (${resp.status}): ${String(detail).slice(0, 200)}`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `${projectCode || 'project'}_AHA_rules.ids`;

      // Save with the File System Access API when available so the
      // user gets a native "Save as…" dialog and can pick the
      // destination. Falls back to the legacy download (→ Downloads
      // folder) on Firefox / Safari. Returns ``cancelled: true`` if
      // the user dismissed the picker — in which case we suppress
      // the success toast and short-circuit. Inside the Emergent
      // preview iframe the helper returns ``manual_action: true``
      // because cross-origin sandboxes block both showSaveFilePicker
      // and the anchor-download trick; we render a sticky toast whose
      // "Save report" button opens /save.html in a top-level tab,
      // which is the only context where the native dialog works.
      const outcome = await saveBlobWithPicker(blob, filename, 'application/xml');
      if (outcome.cancelled) {
        toast.dismiss(t);
        return;
      }
      if (outcome.manual_action) {
        toast.dismiss(t);
        const sizeKb = `${(blob.size / 1024).toFixed(1)} KB`;
        toast.message(`${filename} is ready`, {
          description: `${sizeKb} · Click "Save IDS file" to pick where to save it on your computer.`,
          duration: 30000,
          action: {
            label: 'Save IDS file',
            onClick: () => {
              const opened = openManualDownload(outcome, (progress) => {
                if (progress.type === 'aha-save-complete') {
                  toast.success(`Saved · ${progress.filename}`, { duration: 5000 });
                } else if (progress.type === 'aha-save-cancelled') {
                  toast.message('Save cancelled', { duration: 4000 });
                } else if (progress.type === 'aha-save-error') {
                  toast.error('Could not save the file', {
                    description: progress.error || 'Unknown error',
                    duration: 10000,
                  });
                } else if (progress.type === 'popup-blocked') {
                  toast.error('Pop-up was blocked', {
                    description:
                      'Allow pop-ups for this site so AHA can open the Save dialog. The file opened in a preview tab as a fallback — use Ctrl/Cmd+S there.',
                    duration: 12000,
                  });
                }
              });
              if (!opened) {
                toast.error('Could not open the Save dialog', {
                  description:
                    'Allow pop-ups for this site, or open AHA in its own browser tab (outside the preview).',
                  duration: 12000,
                });
              }
            },
          },
        });
        return;
      }
      toast.dismiss(t);
      const where = outcome.location === 'picked'
        ? `Saved to your chosen location · ${outcome.filename}`
        : `Saved to your Downloads folder · ${outcome.filename}`;
      toast.success(where, {
        description: `${(blob.size / 1024).toFixed(1)} KB · open this file in Solibri, BIMcollab Zoom, IfcTester, or any IDS-aware BIM tool.`,
        duration: 7000,
      });
    } catch (e) {
      toast.dismiss(t);
      toast.error('IDS export failed', {
        description: e.message || 'Unknown error',
        duration: 8000,
      });
      setError(e.message || 'IDS export failed');
    } finally {
      setExportLoading(false);
    }
  };

  // ── Import ────────────────────────────────────────────────────────
  const handleImportClick = () => {
    setError('');
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';   // allow re-selecting the same file later
    if (!file) return;
    setImportLoading(true);
    setError('');
    const t = toast.loading('Parsing IDS file…', {
      description: file.name,
    });
    try {
      const form = new FormData();
      form.append('ids_file', file);
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/ids/import`,
        { method: 'POST', body: form },
      );
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        let detail = txt;
        try { detail = JSON.parse(txt).detail || txt; } catch (_) { /* keep raw */ }
        throw new Error(`IDS import failed (${resp.status}): ${String(detail).slice(0, 200)}`);
      }
      const json = await resp.json();
      const candidateCount = (json.rules || []).length;
      const warningCount = (json.warnings || []).length;
      setPreview({
        rules: (json.rules || []).map((r) => ({ ...r, selected: true })),
        warnings: json.warnings || [],
        summary: json.summary || { total: 0 },
        sourceFile: file.name,
      });
      toast.dismiss(t);
      toast.success(`Parsed ${candidateCount} candidate rule${candidateCount === 1 ? '' : 's'}`, {
        description: warningCount > 0
          ? `${warningCount} warning${warningCount === 1 ? '' : 's'} — review before confirming`
          : 'Review the preview and click Confirm Import to add them.',
        duration: 6000,
      });
    } catch (e) {
      toast.dismiss(t);
      toast.error('IDS import failed', {
        description: e.message || 'Unknown error',
        duration: 8000,
      });
      setError(e.message || 'IDS import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const toggleCandidate = (idx) => {
    setPreview((prev) => prev && {
      ...prev,
      rules: prev.rules.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r),
    });
  };

  const selectAll = () => setPreview((p) => p && {
    ...p, rules: p.rules.map((r) => ({ ...r, selected: true })),
  });
  const deselectAll = () => setPreview((p) => p && {
    ...p, rules: p.rules.map((r) => ({ ...r, selected: false })),
  });

  const handleConfirmImport = async () => {
    if (!preview) return;
    const chosen = preview.rules.filter((r) => r.selected);
    if (chosen.length === 0) {
      setError('Select at least one candidate before confirming.');
      toast.warning('Nothing selected', { description: 'Tick at least one candidate first.' });
      return;
    }
    setConfirmLoading(true);
    setError('');
    const t = toast.loading(`Adding ${chosen.length} rule${chosen.length === 1 ? '' : 's'} to project…`);
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/confirm-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bep_file_name: preview.sourceFile || 'imported.ids',
          manifest: { project_name: projectName, source: 'ids_import' },
          confirmed_rules: chosen,
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        let detail = txt;
        try { detail = JSON.parse(txt).detail || txt; } catch (_) { /* keep raw */ }
        throw new Error(`Confirm failed (${resp.status}): ${String(detail).slice(0, 200)}`);
      }
      setPreview(null);
      toast.dismiss(t);
      toast.success(`Added ${chosen.length} rule${chosen.length === 1 ? '' : 's'} from IDS file`, {
        description: 'Refreshing rule list…',
        duration: 5000,
      });
      if (typeof onImported === 'function') onImported(chosen.length);
    } catch (e) {
      toast.dismiss(t);
      toast.error('Could not add rules', {
        description: e.message || 'Unknown error',
        duration: 8000,
      });
      setError(e.message || 'Confirm failed');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <>
      <Button
        size="sm" variant="outline" className="h-7"
        onClick={handleExport}
        disabled={exportLoading}
        data-testid="export-ids-btn"
        title="Download the active rules as an IDS 1.0 XML file. Any IDS-aware BIM tool (Solibri, BIMcollab, IfcTester) can validate an IFC against this manifest."
      >
        {exportLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileCode className="h-3.5 w-3.5 mr-1" />}
        IDS Export
      </Button>
      {!readOnly && <Button
        size="sm" variant="outline" className="h-7"
        onClick={handleImportClick}
        disabled={importLoading}
        data-testid="import-ids-btn"
        title="Upload an IDS file (e.g. from buildingSMART, Solibri or a peer project) and review parsed candidate rules before adding them to this project."
      >
        {importLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
        IDS Import
      </Button>}
      {!readOnly && <input
        ref={fileInputRef}
        type="file"
        accept=".ids,.xml"
        style={{ display: 'none' }}
        onChange={handleFileChosen}
        data-testid="ids-import-file-input"
      />}

      {error && (
        <Alert variant="destructive" className="basis-full mt-2" data-testid="ids-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ── Import preview modal ─────────────────────────────── */}
      {!readOnly && <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ids-import-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCode className="h-5 w-5 text-accent" /> Review IDS Import
            </DialogTitle>
            <DialogDescription>
              {preview?.summary?.total ?? 0} candidate rules parsed from{' '}
              <span className="font-mono text-foreground">{preview?.sourceFile}</span>. Select the
              ones you want to add to this project — nothing is written until you confirm.
            </DialogDescription>
          </DialogHeader>

          {preview?.warnings?.length > 0 && (
            <Alert className="mb-2" data-testid="ids-import-warnings">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>{preview.warnings.length} warning{preview.warnings.length === 1 ? '' : 's'}:</strong>
                <ul className="mt-1 list-disc pl-5 space-y-0.5">
                  {preview.warnings.slice(0, 6).map((w, i) => <li key={i}>{w}</li>)}
                  {preview.warnings.length > 6 && (
                    <li className="text-muted-foreground">…{preview.warnings.length - 6} more</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-2 my-2">
            <Button size="sm" variant="outline" onClick={selectAll} data-testid="ids-select-all-btn">Select all</Button>
            <Button size="sm" variant="outline" onClick={deselectAll} data-testid="ids-deselect-all-btn">Deselect all</Button>
            <span className="ml-auto text-xs text-muted-foreground" data-testid="ids-selected-count">
              {preview?.rules?.filter((r) => r.selected).length ?? 0} selected
            </span>
          </div>

          <div className="space-y-2">
            {(preview?.rules || []).map((r, idx) => (
              <label
                key={`${r.rule_id}_${idx}`}
                className={`flex gap-3 p-3 rounded border cursor-pointer transition-colors ${
                  r.selected ? 'bg-accent/5 border-accent/30' : 'bg-background border-border'
                }`}
                data-testid={`ids-candidate-${idx}`}
              >
                <input
                  type="checkbox"
                  checked={!!r.selected}
                  onChange={() => toggleCandidate(idx)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate">{r.rule_name || r.rule_id}</p>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${SEVERITY_TONE[r.severity] || ''}`}>
                      {r.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 break-words">{r.description || '—'}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mt-1">
                    id: {r.rule_id} · check: {r.check_type} · field: {r.field_to_check || '—'}
                    {r.pset_name && ` · pset: ${r.pset_name}`}
                    {r.applies_to_discipline && r.applies_to_discipline !== 'all' && ` · discipline: ${r.applies_to_discipline}`}
                    {r.classification_system && ` · system: ${r.classification_system}`}
                    {r.classification_value && ` · code: ${r.classification_value}`}
                  </p>
                </div>
              </label>
            ))}
            {(!preview?.rules || preview.rules.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-6">
                No usable rules found in this IDS file.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPreview(null)} data-testid="ids-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={confirmLoading || !(preview?.rules?.some((r) => r.selected))}
              className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
              data-testid="ids-confirm-import-btn"
            >
              {confirmLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}
    </>
  );
}
